import { fetcher } from '#lib/backendFetcher';
import { validateResponse } from '#lib/validation';

import { orgInfoSchema, orgTeamSchema } from './orgSchemas';

/**
 * Fetches the organization information of the logged-in user and validates it.
 * @returns {Promise<Object>}
 */
export const fetchOrgInfo = async () => {
	const data = await fetcher('/api/v1/platform/orgs/me');
	return validateResponse(orgInfoSchema, data, 'orgInfo');
};

/**
 * Ensures an organization (membership + wallet) exists for the current user.
 * Idempotent login self-heal — safe to call on every login. Returns the org id.
 * @returns {Promise<{orgId: string}>}
 */
export const ensureOrgExists = async () => {
	return await fetcher('/api/v1/platform/auth/ensure-org-exists', { method: 'POST' });
};

/**
 * Fetches all members of the organization (wallet data flattened into each member) and, for
 * admins, the org's pending invites; members get an empty invite list.
 * @returns {Promise<{members: Array<Object>, invites: Array<Object>}>}
 */
export const fetchOrgMembers = async () => {
	const res = await fetcher('/api/v1/platform/orgs/members');
	const members = (res.members ?? []).map(({ wallet, ...member }) => ({
		...member,
		companyCredits: wallet?.companyCredits ?? 0,
		contactCredits: wallet?.contactCredits ?? 0,
		companyMonthlyBudget: wallet?.companyMonthlyBudget ?? 0,
		contactMonthlyBudget: wallet?.contactMonthlyBudget ?? 0,
	}));
	return validateResponse(orgTeamSchema, { members, invites: res.invites ?? [] }, 'orgMembers');
};

/**
 * Invites a new member to the organization by email.
 * @param {string} email - Email address of the user to invite
 * @param {string} role - Role of the new member (e.g. 'admin', 'member')
 * @returns {Promise<Object>}
 */
export const inviteMember = async (email, role) => {
	return await fetcher('/api/v1/platform/orgs/members/invite', {
		method: 'POST',
		body: { email, role },
	});
};

/**
 * Removes a member from the organization, optionally with data transfer.
 * @param {string} userId - ID of the member to remove
 * @param {boolean} [transferData=false] - Whether to transfer the member's data
 * @returns {Promise<Object>}
 */
export const removeMember = async (userId, transferData = false) => {
	return await fetcher(`/api/v1/platform/orgs/members/${userId}?transferData=${transferData}`, {
		method: 'DELETE',
	});
};

/**
 * Extends a pending org invite by 7 days and mails it again.
 * @param {string} inviteId
 * @returns {Promise<{expiresAt: string}>}
 */
export const resendInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/members/invites/${inviteId}/resend`, {
		method: 'POST',
	});
};

/**
 * Withdraws a pending org invite; the backend also deletes the invitee's account if nothing else uses it.
 * @param {string} inviteId
 * @returns {Promise<{accountDeleted: boolean}>}
 */
export const revokeInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/members/invites/${inviteId}`, {
		method: 'DELETE',
	});
};

/**
 * Fetches all pending invitations of the user.
 * @returns {Promise<Object>}
 */
export const fetchPendingInvites = async () => {
	return await fetcher('/api/v1/platform/orgs/invites');
};

/**
 * Accepts an organization invitation.
 * @param {string} inviteId - Invitation ID
 * @returns {Promise<Object>}
 */
export const acceptInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/invites/${inviteId}/accept`, {
		method: 'POST',
	});
};

/**
 * Declines an organization invitation.
 * @param {string} inviteId - Invitation ID
 * @returns {Promise<Object>}
 */
export const declineInvite = async (inviteId) => {
	return await fetcher(`/api/v1/platform/orgs/invites/${inviteId}/decline`, {
		method: 'POST',
	});
};

/**
 * Sets the monthly credit budget for an organization member.
 * @param {string} userId - Member ID
 * @param {Object} budget
 * @param {number} budget.companyMonthlyBudget - Monthly budget for company credits
 * @param {number} budget.contactMonthlyBudget - Monthly budget for contact credits
 * @returns {Promise<Object>}
 */
export const setMemberBudget = async (userId, { companyMonthlyBudget, contactMonthlyBudget }) => {
	return await fetcher(`/api/v1/platform/orgs/members/${userId}/budget`, {
		method: 'PUT',
		body: { companyBudget: companyMonthlyBudget, contactBudget: contactMonthlyBudget },
	});
};

/**
 * Changes the role of an organization member.
 * @param {string} userId - Member ID
 * @param {string} role - New role (e.g. 'admin', 'member')
 * @returns {Promise<Object>}
 */
export const changeMemberRole = async (userId, role) => {
	return await fetcher(`/api/v1/platform/orgs/members/${userId}/role`, {
		method: 'PUT',
		body: { role },
	});
};

/**
 * Transfers credits from one member to another within the organization.
 * @param {Object} params
 * @param {string} params.senderId - ID of the sending member
 * @param {string} params.receiverId - ID of the receiving member
 * @param {string} params.creditType - Type of credits ('company' or 'contact')
 * @param {number} params.amount - Number of credits to transfer
 * @returns {Promise<Object>}
 */
export const transferCredits = async ({ senderId, receiverId, creditType, amount }) => {
	return await fetcher('/api/v1/platform/orgs/members/transfer', {
		method: 'POST',
		body: { senderId, receiverId, creditType, amount },
	});
};

/**
 * Fetches the current credit balance of the organization.
 * @returns {Promise<Object>}
 */
export const fetchOrgCredits = async () => {
	return await fetcher('/api/v1/platform/orgs/credits');
};
